import {
  connectionLabel,
  doorStatusLabel,
  formatTimestamp,
  overrideLabel,
} from "../../lib/format.js";
import type { Door } from "../../types/api.js";

export interface DoorRowProps {
  door: Door;
  busy: string | null;
  canOpen: boolean;
  canHold: boolean;
  canRelease: boolean;
  canViewAudit: boolean;
  onOpen: (door: Door) => void;
  onCheck: (door: Door) => void;
  onActivity: (door: Door) => void;
  onRelease: (door: Door) => void;
  onHold: (door: Door, action: "hold_unlock" | "hold_lock") => void;
}

export function DoorRow({
  door,
  busy,
  canOpen,
  canHold,
  canRelease,
  canViewAudit,
  onOpen,
  onCheck,
  onActivity,
  onRelease,
  onHold,
}: DoorRowProps) {
  const status = doorStatusLabel(door);
  const override = overrideLabel(door);
  const connection = connectionLabel(door);
  const isBusy = busy === door.doorId;

  return (
    <tr>
      <td>
        <div className="cell-name">{door.name}</div>
        <div className="cell-sub mono">{door.doorId}</div>
      </td>
      <td>{door.location}</td>
      <td>
        {/* An icon AND a word. Never colour alone. */}
        <span className={`status tone-${status.tone}`}>
          <span className="status-icon" aria-hidden="true">
            {status.icon}
          </span>
          <span>{status.word}</span>
        </span>
        {!door.isEnabled ? <div className="cell-sub">disabled in the registry</div> : null}
      </td>
      <td>
        <span className={`status tone-${connection.tone}`}>
          <span className="status-icon" aria-hidden="true">
            {connection.icon}
          </span>
          <span>{connection.word}</span>
        </span>
        <div className="cell-sub mono">
          {door.controllerAddress}:{door.deviceId}
        </div>
      </td>
      <td>{door.accessMode}</td>
      <td>
        <span className={`status tone-${override.tone}`}>
          <span className="status-icon" aria-hidden="true">
            {override.icon}
          </span>
          <span>{override.word}</span>
        </span>
        <div className="cell-sub">{override.detail}</div>
      </td>
      <td>
        <div className="stack">
          <span className="mono">{door.lastAction ?? "—"}</span>
          <span className="cell-sub">{formatTimestamp(door.lastActionAt)}</span>
          {door.lastActionBy ? <span className="cell-sub">{door.lastActionBy}</span> : null}
        </div>
      </td>
      <td>
        <div className="actions">
          {/* Inline and always reachable. Release Override is the RECOVERY action - it is never
              buried behind a disclosure. */}
          {canOpen ? (
            <button
              type="button"
              className="btn"
              disabled={isBusy || !door.isEnabled}
              onClick={() => onOpen(door)}
            >
              Open
            </button>
          ) : null}

          <button type="button" className="btn" disabled={isBusy} onClick={() => onCheck(door)}>
            Check
          </button>

          {canViewAudit ? (
            <button type="button" className="btn" onClick={() => onActivity(door)}>
              Activity
            </button>
          ) : null}

          {canRelease && door.overrideActive ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={isBusy}
              onClick={() => onRelease(door)}
            >
              Release Override
            </button>
          ) : null}

          {/* The two persistent overrides sit behind a deliberate second click. A native
              details/summary: no JavaScript toggle (which matters under script-src 'self'),
              and native keyboard and screen-reader behaviour for free. */}
          {canHold ? (
            <details className="disclosure">
              <summary>Override…</summary>
              <div className="disclosure-panel">
                <p className="disclosure-note">
                  Persistent. Survives a power cycle and suspends verification until released.
                </p>
                <button
                  type="button"
                  className="btn btn-block"
                  disabled={isBusy || !door.isEnabled}
                  onClick={() => onHold(door, "hold_unlock")}
                >
                  Hold Unlock
                </button>
                <button
                  type="button"
                  className="btn btn-block"
                  disabled={isBusy || !door.isEnabled}
                  onClick={() => onHold(door, "hold_lock")}
                >
                  Hold Lock
                </button>
              </div>
            </details>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
